/**
 * FigmaLeftRail — sidebar with 3-level accordion: Sport → Country → League
 */
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import useServiceContextHook from '../../../_hooks/service.context.hook';
import { SportsnewContext } from '../../../_context/SportsnewContext';

// SVG sport icons
import gameIconSoccer from '../../../assets/Games/1.svg';
import gameIconBasketball from '../../../assets/Games/2.svg';
import gameIconBaseball from '../../../assets/Games/3.svg';
import gameIconHockey from '../../../assets/Games/4.svg';
import gameIconVolleyball from './assets/valleyball.png';
import { ChevronDown, Trophy, X } from 'lucide-react';
import { NavItemsSkeleton } from '../../../components/Skeleton/Skeleton';

// Launch sports. Esports uses game-title/tournament navigation instead of country/league navigation.
const NAV_SPORT_TABS = [
  { id: 'soccer',     icon: gameIconSoccer,      label: '축구' },
  { id: 'baseball',   icon: gameIconBaseball,    label: '야구' },
  { id: 'basketball', icon: gameIconBasketball,  label: '농구' },
  { id: 'volleyball', icon: gameIconVolleyball,  label: '배구' },
  { id: 'ice-hockey', icon: gameIconHockey,      label: '아이스 하키' },
];

const SPORT_ICON_BY_SLUG = {
  soccer: gameIconSoccer,
  baseball: gameIconBaseball,
  basketball: gameIconBasketball,
  volleyball: gameIconVolleyball,
  'ice-hockey': gameIconHockey,
};
const SPORT_LABEL_BY_SLUG = Object.fromEntries(NAV_SPORT_TABS.map((tab) => [tab.id, tab.label]));
const scopeForTab = (tab) => {
  if (tab === 'LIVE') return 'live';
  if (tab === 'DOMESTIC') return 'domestic';
  if (tab === 'SPECIAL') return 'esports';
  return 'overseas';
};
const sportVisibleForScope = (category, scope) => {
  if (!category || category.active === false) return false;
  if (category.slug === 'esports') return scope === 'live' && category.liveEnabled !== false;
  if (scope === 'live') return category.liveEnabled !== false;
  if (scope === 'domestic') return category.domesticEnabled !== false;
  return category.overseasEnabled !== false;
};
const sportOrderValue = (category) => {
  const value = Number(category?.sortOrder ?? category?.sort_order);
  return Number.isFinite(value) && value > 0 ? value : Number.MAX_SAFE_INTEGER;
};
const fixtureCountValue = (item) => {
  const value = Number(
    item?.fixtureCount
    ?? item?.fixture_count
    ?? item?.liveFixtureCount
    ?? item?.live_fixture_count
    ?? item?.matchCount
    ?? item?.match_count
    ?? item?.count
    ?? item?.total
    ?? 0
  );
  return Number.isFinite(value) && value > 0 ? value : 0;
};
const navSportTabsFromCategories = (categories, scope) => {
  const rows = Array.isArray(categories)
    ? categories
      .filter((category) => sportVisibleForScope(category, scope))
      .slice()
      .sort((a, b) => (
        sportOrderValue(a) - sportOrderValue(b)
        || String(a.slug || '').localeCompare(String(b.slug || ''))
      ))
    : [];
  if (rows.length === 0) return NAV_SPORT_TABS;
  return rows.map((category) => ({
    id: category.slug,
    icon: SPORT_ICON_BY_SLUG[category.slug] || gameIconSoccer,
    isEsports: category.slug === 'esports',
    label: category.nameKo || category.name || SPORT_LABEL_BY_SLUG[category.slug] || category.slug,
    fixtureCount: fixtureCountValue(category),
  }));
};

const normalizeNavSportSlug = (value) => String(value || '').trim().toLowerCase().replace(/_/g, '-');
const allSportNavigationCacheKey = (tab) => `${tab}_all`;
const leagueSportSlug = (league) => normalizeNavSportSlug(
  league?.sportSlug ?? league?.sport_slug ?? league?.sport ?? league?.sportId ?? ''
);
const countriesFromNavigation = (data) => (
  Array.isArray(data) ? data : (data?.countries ?? [])
);
const canSplitNavigationBySport = (data) => (
  countriesFromNavigation(data).some((country) => (
    (country?.leagues ?? []).some((league) => leagueSportSlug(league))
  ))
);
const splitNavigationForSport = (data, sportSlug) => {
  const target = normalizeNavSportSlug(sportSlug);
  if (!target) return countriesFromNavigation(data);
  return countriesFromNavigation(data)
    .map((country) => {
      const leagues = (country?.leagues ?? [])
        .filter((league) => leagueSportSlug(league) === target);
      if (leagues.length === 0) return null;
      return {
        ...country,
        leagues,
        leagueCount: leagues.length,
        fixtureCount: leagues.reduce((sum, league) => sum + Number(league.fixtureCount || 0), 0),
      };
    })
    .filter((country) => country && country.fixtureCount > 0);
};

const navigationItemsForSport = (data, sportSlug, options = {}) => {
  if (Array.isArray(data)) return data;
  if (sportSlug === 'esports') {
    const gameTitleItems = data?.gameTitles ?? data?.games ?? data?.items;
    if (Array.isArray(gameTitleItems)) return gameTitleItems;
    return options.allowEsportsCountryFallback ? (data?.countries ?? []) : [];
  }
  if (canSplitNavigationBySport(data)) {
    return splitNavigationForSport(data, sportSlug);
  }
  return data?.countries ?? [];
};

const canUseAllSportNavigationForSport = (tab, sportSlug, data) => {
  if (tab === 'LIVE' && sportSlug === 'esports') return false;
  return canSplitNavigationBySport(data);
};

const esportsIconUrl = (item) => item?.iconUrl || item?.icon || item?.gameTitleIconUrl || null;
const EsportsEmojiIcon = ({ className, size = 18, style = {} }) => (
  <span
    className={className}
    aria-hidden="true"
    style={{
      width: size,
      height: size,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      fontSize: size,
      lineHeight: 1,
      ...style,
    }}
  >
    🎮
  </span>
);
const EsportsIcon = ({ item, className, size = 18, style = {} }) => {
  const iconUrl = esportsIconUrl(item);
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt=""
        className={className}
        style={{ width: size, height: size, objectFit: 'contain', ...style }}
        onError={(event) => { event.currentTarget.style.display = 'none'; }}
      />
    );
  }
  return <EsportsEmojiIcon className={className} size={size} style={style} />;
};

const INTERNATIONAL_FLAG_URL = 'https://flagpedia.net/data/org/w40/eu.png';
const INTERNATIONAL_FLAG_KEYS = new Set(['int', 'international', 'world', 'global', 'europe', '국제', '세계']);

const normalizedFlagKey = (value) => String(value || '').trim().toLowerCase();
const isInternationalFlagKey = (...values) => values.some((value) => INTERNATIONAL_FLAG_KEYS.has(normalizedFlagKey(value)));
const flagSrcForCode = (code) => (
  code
    ? `https://flagcdn.com/w40/${code}.png`
    : null
);

// Alpha-3 → alpha-2 country code map for flagcdn.com
const ALPHA3_TO_ALPHA2 = {
  int: null, afg: 'af', alb: 'al', dza: 'dz', and: 'ad', ago: 'ao', arg: 'ar',
  arm: 'am', aus: 'au', aut: 'at', aze: 'az', bhs: 'bs', bhr: 'bh', bgd: 'bd',
  blr: 'by', bel: 'be', blz: 'bz', ben: 'bj', btn: 'bt', bol: 'bo', bih: 'ba',
  bwa: 'bw', bra: 'br', brn: 'bn', bgr: 'bg', bfa: 'bf', bdi: 'bi', khm: 'kh',
  cmr: 'cm', can: 'ca', cpv: 'cv', caf: 'cf', tcd: 'td', chl: 'cl', chi: 'cl',
  chn: 'cn', col: 'co', com: 'km', cod: 'cd', cog: 'cg', cri: 'cr', hrv: 'hr',
  cub: 'cu', cyp: 'cy', cze: 'cz', dnk: 'dk', dji: 'dj', dom: 'do', ecu: 'ec',
  egy: 'eg', slv: 'sv', eng: 'gb-eng', gnq: 'gq', eri: 'er', est: 'ee', eth: 'et',
  fji: 'fj', fin: 'fi', fra: 'fr', fro: 'fo', gab: 'ga', gmb: 'gm', geo: 'ge',
  deu: 'de', gha: 'gh', grc: 'gr', gtm: 'gt', gin: 'gn', gnb: 'gw', guy: 'gy',
  hti: 'ht', hnd: 'hn', hun: 'hu', isl: 'is', ind: 'in', idn: 'id', irn: 'ir',
  irq: 'iq', irl: 'ie', isr: 'il', ita: 'it', jam: 'jm', jpn: 'jp', jor: 'jo',
  kaz: 'kz', ken: 'ke', prk: 'kp', kor: 'kr', kos: 'xk', kwt: 'kw', kgz: 'kg',
  lao: 'la', lva: 'lv', lbn: 'lb', lso: 'ls', lbr: 'lr', lby: 'ly', lie: 'li',
  ltu: 'lt', lux: 'lu', mac: 'mo', mkd: 'mk', mdg: 'mg', mwi: 'mw', mys: 'my',
  mdv: 'mv', mli: 'ml', mlt: 'mt', mar: 'ma', mus: 'mu', mex: 'mx', mda: 'md',
  mco: 'mc', mng: 'mn', mne: 'me', moz: 'mz', mmr: 'mm', nam: 'na', npl: 'np',
  nld: 'nl', nzl: 'nz', nic: 'ni', ner: 'ne', nga: 'ng', nor: 'no', omn: 'om',
  pak: 'pk', pan: 'pa', png: 'pg', pry: 'py', per: 'pe', phl: 'ph', pol: 'pl',
  prt: 'pt', qat: 'qa', rou: 'ro', rus: 'ru', rwa: 'rw', kna: 'kn', lca: 'lc',
  vct: 'vc', wsm: 'ws', smr: 'sm', stp: 'st', sau: 'sa', sen: 'sn', srb: 'rs',
  sle: 'sl', sgp: 'sg', svk: 'sk', svn: 'si', slb: 'sb', som: 'so', zaf: 'za',
  esp: 'es', lka: 'lk', sdn: 'sd', sur: 'sr', swz: 'sz', swe: 'se', sui: 'ch',
  che: 'ch', syr: 'sy', twn: 'tw', tjk: 'tj', tza: 'tz', tha: 'th', tls: 'tl',
  tgo: 'tg', tto: 'tt', tun: 'tn', tur: 'tr', tkm: 'tm', uga: 'ug', ukr: 'ua',
  are: 'ae', gbr: 'gb', usa: 'us', ury: 'uy', uzb: 'uz', ven: 've', vnm: 'vn',
  yem: 'ye', zmb: 'zm', zwe: 'zw',
};

const getFlagCode = (countryKey) => {
  if (!countryKey) return null;
  if (isInternationalFlagKey(countryKey)) return null;
  const key = normalizedFlagKey(countryKey);
  return key in ALPHA3_TO_ALPHA2 ? ALPHA3_TO_ALPHA2[key] : key.slice(0, 2);
};
const resolveFlagCode = (flagCode, countryKey) => {
  const raw = normalizedFlagKey(flagCode);
  if (isInternationalFlagKey(raw, countryKey)) return null;
  if (raw.includes('-')) return raw;
  return getFlagCode(raw || countryKey);
};
const resolveFlagSrc = (flagCode, countryKey, countryName) => (
  isInternationalFlagKey(flagCode, countryKey, countryName)
    ? INTERNATIONAL_FLAG_URL
    : flagSrcForCode(resolveFlagCode(flagCode, countryKey))
);

// Static domestic / special mock data
const DOMESTIC_MOCKS = [
  { id: 'bs_d', icon: gameIconBaseball,    name: '야구', count: 8, subs: [
    { name: '대한민국 KBO', count: 5, code: 'kr' },
    { name: '일본 NPB',    count: 3, code: 'jp' },
  ] },
  { id: 'sc_d', icon: gameIconSoccer,      name: '축구', count: 6, subs: [
    { name: 'K리그 1', count: 4, code: 'kr' },
    { name: 'K리그 2', count: 2, code: 'kr' },
  ] },
  { id: 'bb_d', icon: gameIconBasketball,  name: '농구', count: 4, subs: [
    { name: 'KBL',  count: 3, code: 'kr' },
    { name: 'WKBL', count: 1, code: 'kr' },
  ] },
  { id: 'vb_d', icon: gameIconVolleyball,  name: '배구', count: 2, subs: [
    { name: 'V-리그 (남)', count: 1, code: 'kr' },
    { name: 'V-리그 (여)', count: 1, code: 'kr' },
  ] },
];


const TAB_INDEX_MAP = { INTERNATIONAL: 0, LIVE: 1, DOMESTIC: 2, SPECIAL: 3 };

const FigmaLeftRail = ({
  categories = [], selectedSport, setSelectedSport,
  totalCount, getCountForSport, getSportIcon,
  bettingType, setBettingType,
  popularMode, setPopularMode,
  mobileOpen,
  onMobileClose,
}) => {
  const { services } = useServiceContextHook();
  const SportsnewService = services?.SportsnewService;
  const {
    categories: sportsnewCategories = [],
    selectedLeagueId: ctxLeagueId,
    setSelectedLeagueId: setSnewLeagueId,
    selectedSport: ctxSport,
    setSelectedSport: setSnewSport,
    selectedGameTitleId: ctxGameTitleId,
    setSelectedGameTitleId: setSnewGameTitleId,
    selectedTournamentId: ctxTournamentId,
    setSelectedTournamentId: setSnewTournamentId,
    setDisplayScope,
  } = useContext(SportsnewContext) || {};
  const navigate = useNavigate();

  // ── Mode tab ──────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(() => {
    if (bettingType === 'DOMESTIC') return 'DOMESTIC';
    if (bettingType === 'SPECIAL')  return 'SPECIAL';
    if (bettingType === 'LIVE')     return 'LIVE';
    return 'INTERNATIONAL';
  });
  const currentScope = scopeForTab(activeTab);
  const effectiveCategories = useMemo(
    () => (Array.isArray(sportsnewCategories) && sportsnewCategories.length > 0 ? sportsnewCategories : categories),
    [sportsnewCategories, categories]
  );
  const navSportTabs = useMemo(
    () => navSportTabsFromCategories(effectiveCategories, currentScope),
    [effectiveCategories, currentScope]
  );

  useEffect(() => {
    if (!bettingType) return;
    setActiveTab(bettingType);
    setDisplayScope?.(scopeForTab(bettingType));
  }, [bettingType, setDisplayScope]);

  // ── 3-level accordion state ────────────────────────────
  const [expandedSport, setExpandedSport]   = useState(null);   // sport slug
  const [expandedCountry, setExpandedCountry] = useState(null); // countryId
  const [expandedEsportsGame, setExpandedEsportsGame] = useState(null);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);

  // Cache: { [`${tab}_${sportSlug}`]: countries[] }
  const [navCache, setNavCache] = useState({});
  const [loadingFor, setLoadingFor] = useState(null); // sport slug currently loading
  const [popularLeagues, setPopularLeagues] = useState([]);
  const [popularLoading, setPopularLoading] = useState(false);

  // Domestic / Special accordion (mock data)
  const [expandedMock, setExpandedMock] = useState(null);
  const [expandedSub,  setExpandedSub]  = useState(null);

  // Prevents double-fetch for esports nav (fetched once per session)
  const esportsNavFetchedRef = useRef(false);
  // Tracks which sport nav keys have been requested to avoid duplicate in-flight requests
  const prefetchRequestedRef = useRef(new Set());
  const preserveSelectionOnTabChangeRef = useRef(false);

  // Reset drill-down when tab changes
  useEffect(() => {
    if (preserveSelectionOnTabChangeRef.current) {
      preserveSelectionOnTabChangeRef.current = false;
      return;
    }
    setExpandedSport(null);
    setExpandedCountry(null);
    setExpandedEsportsGame(null);
    setSelectedLeagueId(null);
    setSnewLeagueId?.(null);
    setSnewGameTitleId?.(null);
    setSnewTournamentId?.(null);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror external context clear (e.g. sport tab click in InternationalPage)
  useEffect(() => {
    if (ctxLeagueId == null) setSelectedLeagueId(null);
  }, [ctxLeagueId]);

  // Sync accordion with external sport selection (top tab click in InternationalPage)
  useEffect(() => {
    const slug = ctxSport?.slug ?? null;
    if (slug === expandedSport) return;
    setExpandedSport(slug);
    setExpandedCountry(null);
    setSelectedLeagueId(null);
    // Fetch nav if not already cached/requested
    if (slug && SportsnewService && (activeTab === 'INTERNATIONAL' || activeTab === 'LIVE' || activeTab === 'DOMESTIC')) {
      const mode = activeTab === 'LIVE' ? 'live' : 'prematch';
      const cacheKey = `${activeTab}_${slug}`;
      const allKey = allSportNavigationCacheKey(activeTab);
      const allData = navCache[allKey];
      if (canUseAllSportNavigationForSport(activeTab, slug, allData)) {
        setNavCache((prev) => (
          prev[cacheKey] ? prev : { ...prev, [cacheKey]: navigationItemsForSport(allData, slug) }
        ));
        return;
      }
      if (!prefetchRequestedRef.current.has(cacheKey)) {
        prefetchRequestedRef.current.add(cacheKey);
        SportsnewService.getNavigation({ mode, sport: slug, scope: scopeForTab(activeTab) })
          .then((res) => setNavCache((prev) => ({
            ...prev,
            [cacheKey]: navigationItemsForSport(res?.data, slug, { allowEsportsCountryFallback: slug === 'esports' }),
          })))
          .catch(() => setNavCache((prev) => ({ ...prev, [cacheKey]: [] })));
      }
    }
  }, [ctxSport]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fetch one all-sport navigation snapshot and split it client-side when
  // the API provides league sportSlug. This avoids a sport-count fanout on
  // every tab entry while keeping per-sport fetch as fallback for older API
  // responses that cannot be split safely.
  useEffect(() => {
    if (!SportsnewService || (activeTab !== 'INTERNATIONAL' && activeTab !== 'LIVE' && activeTab !== 'DOMESTIC')) return;
    const mode = activeTab === 'LIVE' ? 'live' : 'prematch';
    const allKey = allSportNavigationCacheKey(activeTab);
    if (navCache[allKey] || prefetchRequestedRef.current.has(allKey)) return;
    prefetchRequestedRef.current.add(allKey);
    SportsnewService.getNavigation({ mode, scope: currentScope })
      .then((res) => {
        const data = res?.data;
        setNavCache((prev) => {
          const next = { ...prev, [allKey]: data };
          if (canSplitNavigationBySport(data)) {
            navSportTabs.forEach(({ id: sportSlug }) => {
              if (activeTab === 'LIVE' && sportSlug === 'esports') return;
              next[`${activeTab}_${sportSlug}`] = navigationItemsForSport(data, sportSlug);
            });
          }
          return next;
        });
      })
      .catch(() => {
        prefetchRequestedRef.current.delete(allKey);
      });
  }, [activeTab, SportsnewService, navSportTabs, currentScope]); // eslint-disable-line react-hooks/exhaustive-deps

  // LIVE eSports must not be derived from all-sport navigation because that
  // payload can contain non-eSports countries/leagues. Prefetch the dedicated
  // sport navigation so the collapsed count is visible before the first click.
  useEffect(() => {
    if (!SportsnewService || activeTab !== 'LIVE') return;
    if (!navSportTabs.some((sport) => sport.id === 'esports')) return;
    const cacheKey = 'LIVE_esports';
    if (navCache[cacheKey] || prefetchRequestedRef.current.has(cacheKey)) return;
    prefetchRequestedRef.current.add(cacheKey);
    SportsnewService.getNavigation({ mode: 'live', sport: 'esports', scope: 'live' })
      .then((res) => setNavCache((prev) => (
        prev[cacheKey]
          ? prev
          : {
              ...prev,
              [cacheKey]: navigationItemsForSport(res?.data, 'esports', {
                allowEsportsCountryFallback: true,
              }),
            }
      )))
      .catch(() => {
        prefetchRequestedRef.current.delete(cacheKey);
      });
  }, [activeTab, SportsnewService, navSportTabs, navCache]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!SportsnewService) {
      setPopularLeagues([]);
      setPopularLoading(false);
      return;
    }

    let cancelled = false;
    const request = activeTab === 'SPECIAL'
      ? { mode: 'prematch', scope: 'esports', sport: 'esports', limit: 10 }
      : { mode: activeTab === 'LIVE' ? 'live' : 'prematch', scope: currentScope, limit: 10 };
    setPopularLoading(true);
    SportsnewService.getFeaturedLeagues(request)
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : (res?.data?.rows ?? res?.data?.leagues ?? []);
        setPopularLeagues(activeTab === 'SPECIAL'
          ? rows.filter((league) => league?.sportSlug === 'esports' && league?.gameTitleId)
          : rows.filter((league) => league?.sportSlug && league.sportSlug !== 'esports'));
      })
      .catch(() => {
        if (!cancelled) setPopularLeagues([]);
      })
      .finally(() => {
        if (!cancelled) setPopularLoading(false);
      });

    return () => { cancelled = true; };
  }, [SportsnewService, activeTab, currentScope]);

  // Auto-fetch esports navigation when SPECIAL tab is active (once per session)
  useEffect(() => {
    if (activeTab !== 'SPECIAL' || !SportsnewService || esportsNavFetchedRef.current) return;
    esportsNavFetchedRef.current = true;
    setLoadingFor('esports');
    SportsnewService.getNavigation({ mode: 'prematch', sport: 'esports', scope: 'esports' })
      .then((res) => {
        const d = res?.data;
        const raw = navigationItemsForSport(d, 'esports') ?? (Array.isArray(d) ? d : []);
        setNavCache((prev) => ({ ...prev, 'SPECIAL_esports': Array.isArray(raw) ? raw : [] }));
      })
      .catch(() => {
        setNavCache((prev) => ({ ...prev, 'SPECIAL_esports': [] }));
        esportsNavFetchedRef.current = false; // allow retry on error
      })
      .finally(() => setLoadingFor(null));
  }, [activeTab, SportsnewService]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──────────────────────────────────────────

  const closeMobileRail = () => {
    if (mobileOpen) onMobileClose?.();
  };

  const loadSportNavigation = async (tab, sportSlug) => {
    const cacheKey = `${tab}_${sportSlug}`;
    if (navCache[cacheKey] || !SportsnewService) return;
    const allKey = allSportNavigationCacheKey(tab);
    const allData = navCache[allKey];
    if (tab !== 'SPECIAL' && canUseAllSportNavigationForSport(tab, sportSlug, allData)) {
      setNavCache((prev) => (
        prev[cacheKey] ? prev : { ...prev, [cacheKey]: navigationItemsForSport(allData, sportSlug) }
      ));
      return;
    }
    setLoadingFor(sportSlug);
    try {
      const mode = tab === 'LIVE' ? 'live' : 'prematch';
      const res = await SportsnewService.getNavigation({ mode, sport: sportSlug, scope: scopeForTab(tab) });
      setNavCache((prev) => (
        prev[cacheKey]
          ? prev
          : {
              ...prev,
              [cacheKey]: navigationItemsForSport(res?.data, sportSlug, {
                allowEsportsCountryFallback: sportSlug === 'esports',
              }),
            }
      ));
    } catch {
      setNavCache((prev) => (prev[cacheKey] ? prev : { ...prev, [cacheKey]: [] }));
    } finally {
      setLoadingFor((prev) => (prev === sportSlug ? null : prev));
    }
  };

  const handleTabClick = (type) => {
    setActiveTab(type);
    setDisplayScope?.(scopeForTab(type));
    setSnewSport?.(type === 'SPECIAL' ? 'esports' : null);
    setSnewLeagueId?.(null);
    setSnewGameTitleId?.(null);
    setSnewTournamentId?.(null);
    setExpandedMock(null);
    setExpandedSub(null);
    setExpandedEsportsGame(null);
    setBettingType?.(type);
    navigate(type === 'LIVE' ? '/sports?type=LIVE' : `/sports?type=${type}`, { replace: true });
    closeMobileRail();
  };

  const handleSportClick = async (sportSlug) => {
    // Collapse if already open
    if (expandedSport === sportSlug) {
      setExpandedSport(null);
      setExpandedCountry(null);
      setExpandedEsportsGame(null);
      setSelectedLeagueId(null);
      setSnewLeagueId?.(null);
      setSnewSport?.(null);
      setSnewGameTitleId?.(null);
      setSnewTournamentId?.(null);
      return;
    }

    // Expand — reset child selections
    setExpandedSport(sportSlug);
    setExpandedCountry(null);
    setExpandedEsportsGame(null);
    setSelectedLeagueId(null);
    setSnewLeagueId?.(null);
    setSnewGameTitleId?.(null);
    setSnewTournamentId?.(null);
    setSnewSport?.({ slug: sportSlug });

    await loadSportNavigation(activeTab, sportSlug);
  };

  const handleCountryClick = (countryId) => {
    setExpandedCountry((prev) => {
      if (prev === countryId) {
        setSelectedLeagueId(null);
        setSnewLeagueId?.(null);
        return null;
      }
      return countryId;
    });
  };

  const handleLeagueClick = (leagueId) => {
    setSelectedLeagueId((prev) => {
      const next = prev === leagueId ? null : leagueId;
      setSnewLeagueId?.(next);
      setSnewGameTitleId?.(null);
      setSnewTournamentId?.(null);
      return next;
    });
    closeMobileRail();
  };

  const selectEsportsGame = (gameId, game, tournament = null) => {
    const nextGameId = game.gameTitleId ?? game.countryId ?? gameId;
    const nextTournamentId = tournament
      ? (tournament.tournamentId ?? tournament.leagueId ?? tournament.id ?? null)
      : null;
    setSnewSport?.({ slug: 'esports' });
    setSnewLeagueId?.(null);
    setSelectedLeagueId(null);
    setSnewGameTitleId?.(nextGameId);
    setSnewTournamentId?.(nextTournamentId);
    if (tournament) closeMobileRail();
  };

  const handleEsportsGameClick = (gameId, game) => {
    const nextGameId = game.gameTitleId ?? game.countryId ?? gameId;
    setExpandedEsportsGame((prev) => (prev === nextGameId ? null : nextGameId));
    selectEsportsGame(gameId, game);
  };

  const handlePopularLeagueClick = async (league) => {
    const sportSlug = league?.sportSlug;
    const leagueId = league?.leagueId;
    if (!sportSlug) return;

    if (sportSlug === 'esports' || league?.gameTitleId) {
      const gameTitleId = league?.gameTitleId;
      if (!gameTitleId) return;
      const tournamentId = league?.tournamentId ?? null;
      if (activeTab !== 'SPECIAL') preserveSelectionOnTabChangeRef.current = true;
      setActiveTab('SPECIAL');
      setBettingType?.('SPECIAL');
      setPopularMode?.(false);
      setDisplayScope?.('esports');
      setExpandedSport('esports');
      setExpandedCountry(null);
      setExpandedEsportsGame(gameTitleId);
      setSelectedLeagueId(null);
      setSnewSport?.({ slug: 'esports' });
      setSnewLeagueId?.(null);
      setSnewGameTitleId?.(gameTitleId);
      setSnewTournamentId?.(tournamentId);
      navigate('/sports?type=SPECIAL', { replace: true });
      closeMobileRail();
      await loadSportNavigation('SPECIAL', 'esports');
      return;
    }

    if (!leagueId) return;

    const nextTab = activeTab === 'LIVE' ? 'LIVE' : activeTab === 'DOMESTIC' ? 'DOMESTIC' : 'INTERNATIONAL';
    if (activeTab !== nextTab) preserveSelectionOnTabChangeRef.current = true;
    setActiveTab(nextTab);
    setBettingType?.(nextTab);
    setPopularMode?.(false);
    setDisplayScope?.(scopeForTab(nextTab));
    setExpandedSport(sportSlug);
    setExpandedCountry(league.countryId ?? null);
    setSelectedLeagueId(leagueId);
    setSnewSport?.({ slug: sportSlug });
    setSnewLeagueId?.(leagueId);
    setSnewGameTitleId?.(null);
    setSnewTournamentId?.(null);
    navigate(nextTab === 'LIVE' ? '/sports?type=LIVE' : `/sports?type=${nextTab}`, { replace: true });
    closeMobileRail();
    await loadSportNavigation(nextTab, sportSlug);
  };

  // ── Render helpers ─────────────────────────────────────

  const renderEsportsGames = (games) => {
    // Dedup games by their resolved ID
    const gameMap = new Map();
    for (const g of games) {
      const gid = g.gameTitleId ?? g.countryId ?? g.id;
      if (gid != null && !gameMap.has(gid)) gameMap.set(gid, g);
    }
    return Array.from(gameMap.values()).map((game) => {
      const gameId = game.gameTitleId ?? game.countryId ?? game.id;
      const isExpanded = expandedEsportsGame === gameId;
      const tournaments = [...new Map((game.tournaments ?? game.leagues ?? []).map((item) => [
        item.tournamentId ?? item.leagueId ?? item.id ?? item.name,
        item,
      ])).values()];
      const isSelectedGame = String(ctxGameTitleId ?? '') === String(gameId);
      const gameName = game.displayName || game.name || game.shortName || 'E-스포츠';

      return (
        <div key={gameId} className="zxsb_nav_sport_wrapper">
          <button
            type="button"
            className={`zxsb_nav_sport_item ${isExpanded || isSelectedGame ? 'expanded' : ''}`}
            onClick={() => handleEsportsGameClick(gameId, game)}
          >
            <EsportsIcon item={game} className="zxsb_nav_sport_item_icon" size={18} />
            <span className="zxsb_nav_sport_item_label">{gameName}</span>
            {game.fixtureCount > 0 && (
              <span className="zxsb_nav_sport_item_count">{game.fixtureCount}</span>
            )}
            <ChevronDown size={14} className={`zxsb_nav_chevron ${isExpanded ? 'rotated' : ''}`} />
          </button>

          {isExpanded && tournaments.length > 0 && (
            <div className="zxsb_nav_countries">
              {tournaments.map((tournament) => (
                <button
                  key={tournament.tournamentId ?? tournament.leagueId ?? tournament.id ?? tournament.name}
                  type="button"
                  className={`zxsb_nav_league_item ${String(ctxTournamentId ?? '') === String(tournament.tournamentId ?? tournament.leagueId ?? tournament.id ?? '') ? 'selected' : ''}`}
                  onClick={() => selectEsportsGame(gameId, game, tournament)}
                >
                  <span className="zxsb_nav_item_name">{tournament.displayName || tournament.name}</span>
                  <span className="zxsb_nav_item_count">{tournament.fixtureCount}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    });
  };

  // 2-level accordion for SPECIAL (E-스포츠) tab — dynamic from nav API
  const renderEsportsPanel = () => {
    const games = navCache['SPECIAL_esports'] ?? [];
    const isLoading = loadingFor === 'esports';

    if (isLoading) return <NavItemsSkeleton count={6} />;

    if (games.length === 0) {
      return <div className="zxsb_nav_status">데이터 없음</div>;
    }

    return renderEsportsGames(games);
  };

  // 3-level accordion for INTERNATIONAL / LIVE tabs
  const renderDynamicPanel = () => (
    navSportTabs.map((sport) => {
      const isSportExpanded = expandedSport === sport.id;
      const cacheKey = `${activeTab}_${sport.id}`;
      const rawCountries = navCache[cacheKey] ?? [];
      if (sport.id === 'esports') {
        const games = rawCountries;
        const isLoading = loadingFor === sport.id;
        const sportTotal = games.reduce((sum, game) => sum + fixtureCountValue(game), 0) || fixtureCountValue(sport);

        return (
          <div key={sport.id} className="zxsb_nav_sport_wrapper">
            <button
              type="button"
              className={`zxsb_nav_sport_item ${isSportExpanded ? 'expanded' : ''}`}
              onClick={() => handleSportClick(sport.id)}
            >
              <EsportsEmojiIcon size={18} />
              <span className="zxsb_nav_sport_item_label">{sport.label}</span>
              {sportTotal > 0 && (
                <span className="zxsb_nav_sport_item_count">{sportTotal}</span>
              )}
              <ChevronDown size={14} className={`zxsb_nav_chevron ${isSportExpanded ? 'rotated' : ''}`} />
            </button>

            {isSportExpanded && (
              <div className="zxsb_nav_countries">
                {isLoading ? (
                  <NavItemsSkeleton count={5} />
                ) : games.length === 0 ? (
                  <div className="zxsb_nav_status">데이터 없음</div>
                ) : (
                  renderEsportsGames(games)
                )}
              </div>
            )}
          </div>
        );
      }
      // Dedup countries by countryId — API may return duplicates
      const countryMap = new Map();
      for (const c of rawCountries) {
        if (c.countryId != null && !countryMap.has(c.countryId)) countryMap.set(c.countryId, c);
      }
      const countries = Array.from(countryMap.values());
      const isLoading = loadingFor === sport.id;
      const sportTotal = countries.reduce((s, c) => s + fixtureCountValue(c), 0) || fixtureCountValue(sport);

      return (
        <div key={sport.id} className="zxsb_nav_sport_wrapper">
          {/* Level 1 — Sport */}
          <button
            type="button"
            className={`zxsb_nav_sport_item ${isSportExpanded ? 'expanded' : ''}`}
            onClick={() => handleSportClick(sport.id)}
          >
            <img src={sport.icon} alt={sport.label} className="zxsb_nav_sport_item_icon" />
            <span className="zxsb_nav_sport_item_label">{sport.label}</span>
            {sportTotal > 0 && (
              <span className="zxsb_nav_sport_item_count">{sportTotal}</span>
            )}
            <ChevronDown size={14} className={`zxsb_nav_chevron ${isSportExpanded ? 'rotated' : ''}`} />
          </button>

          {/* Level 2 — Countries */}
          {isSportExpanded && (
            <div className="zxsb_nav_countries">
              {isLoading ? (
                <NavItemsSkeleton count={5} />
              ) : countries.length === 0 ? (
                <div className="zxsb_nav_status">데이터 없음</div>
              ) : (
                countries.map((country) => {
                  const isCountryExpanded = expandedCountry === country.countryId;
                  const countryFlagSrc = resolveFlagSrc(
                    country.flagCode,
                    country.countryKey,
                    country.displayName || country.name,
                  );

                  return (
                    <div key={country.countryId} className="zxsb_nav_country_wrapper">
                      <button
                        type="button"
                        className={`zxsb_nav_country_item ${isCountryExpanded ? 'expanded' : ''}`}
                        onClick={() => handleCountryClick(country.countryId)}
                      >
                        {countryFlagSrc ? (
                          <img
                            src={countryFlagSrc}
                            alt=""
                            className="zxsb_league_flag"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <span className="zxsb_nav_flag_placeholder" />
                        )}
                        <span className="zxsb_nav_item_name">{country.displayName || country.name}</span>
                        <span className="zxsb_nav_item_count">{country.fixtureCount}</span>
                        <ChevronDown size={12} className={`zxsb_nav_chevron ${isCountryExpanded ? 'rotated' : ''}`} />
                      </button>

                      {/* Level 3 — Leagues */}
                      {isCountryExpanded && (
                        <div className="zxsb_nav_leagues">
                          {country.leagues && country.leagues.length > 0 ? (
                            [...new Map(country.leagues.map((l) => [l.leagueId, l])).values()].map((league) => (
                              <button
                                key={league.leagueId}
                                type="button"
                                className={`zxsb_nav_league_item ${selectedLeagueId === league.leagueId ? 'selected' : ''}`}
                                onClick={() => handleLeagueClick(league.leagueId)}
                              >
                                <span className="zxsb_nav_item_name">{league.displayName || league.name}</span>
                                <span className="zxsb_nav_item_count">{league.fixtureCount}</span>
                              </button>
                            ))
                          ) : (
                            <div className="zxsb_nav_league_empty">리그 정보 없음</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      );
    })
  );

  // 2-level accordion for DOMESTIC / SPECIAL (mock data)
  const renderMockSportList = (sportsData) => sportsData.map((sport) => {
    const isExpanded = expandedMock === sport.id;
    const IconComponent = sport.icon;

    return (
      <div key={sport.id} className="zxsb_rail_sport_wrapper">
        <button
          type="button"
          onClick={() => setExpandedMock(isExpanded ? null : sport.id)}
          className={`zxsb_rail_item_sport ${isExpanded ? 'expanded' : ''}`}
        >
          {typeof sport.icon === 'string' ? (
            <img src={sport.icon} alt={sport.name} className="zxsb_rail_icon_img" />
          ) : (
            <IconComponent className="zxsb_rail_icon_img zxsb_rail_lucide_icon" style={{ width: '18px', height: '18px' }} />
          )}
          <span className="zxsb_rail_name">{sport.name}</span>
          <span className="zxsb_rail_count">{sport.count}</span>
        </button>

        <div className={`zxsb_rail_sub_container ${isExpanded ? 'expanded' : ''}`}>
          {sport.subs && sport.subs.length > 0 && (
            <div className="zxsb_rail_sub_list_wrapper">
              {sport.subs.map((sub, idx) => (
                <div key={idx} className="zxsb_rail_sub_wrapper">
                  <button
                    type="button"
                    className="zxsb_rail_sub_item"
                    onClick={() => setExpandedSub((prev) => prev && prev.sportId === sport.id && prev.subIdx === idx ? null : { sportId: sport.id, subIdx: idx })}
                  >
                    <img src={`https://flagcdn.com/w40/${sub.code}.png`} alt={sub.name} className="zxsb_league_flag" />
                    <span className="zxsb_rail_name">{sub.name}</span>
                    <span className="zxsb_rail_count">{sub.count}</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  });

  // ── JSX ───────────────────────────────────────────────

  return (
    <div className={`zxsb_rail ${mobileOpen ? 'zxsb_rail_mobile_open' : ''}`}>
      {mobileOpen && (
        <div className="zxsb_mobile_rail_header">
          <strong>스포츠 메뉴</strong>
          <button type="button" onClick={onMobileClose} aria-label="스포츠 메뉴 닫기">
            <X size={18} />
            <span>닫기</span>
          </button>
        </div>
      )}

      {/* Popular Leagues */}
      {(popularLoading || popularLeagues.length > 0) && (
        <div className="zxsb_rail_popular_section">
          <div className="zxsb_rail_popular_header">
            <Trophy size={20} className="zxsb_rail_popular_header_icon" />
            <span>인기리그</span>
          </div>
          <div className="zxsb_rail_popular_list">
            {popularLoading ? (
              <NavItemsSkeleton count={4} />
            ) : (
              popularLeagues.map((league) => {
                const isEsportsPopular = league.sportSlug === 'esports' || league.gameTitleId;
                const flagSrc = isEsportsPopular ? null : resolveFlagSrc(
                  league.flagCode,
                  league.countryKey,
                  league.countryName || league.countryNameKo || league.country,
                );
                const popularKey = isEsportsPopular
                  ? `esports:${league.gameTitleId ?? 'game'}:${league.tournamentId ?? 'all'}`
                  : `league:${league.leagueId}`;
                const isSelectedPopular = isEsportsPopular
                  ? String(ctxGameTitleId ?? '') === String(league.gameTitleId ?? '')
                    && String(ctxTournamentId ?? '') === String(league.tournamentId ?? '')
                  : selectedLeagueId === league.leagueId;
                return (
                  <button
                    key={popularKey}
                    type="button"
                    className={`zxsb_rail_popular_item ${isSelectedPopular ? 'selected' : ''}`}
                    onClick={() => handlePopularLeagueClick(league)}
                  >
                    <div className={`zxsb_rail_popular_icons ${isEsportsPopular ? 'zxsb_rail_popular_icons_single' : ''}`}>
                      {isEsportsPopular ? (
                        <EsportsIcon item={league} className="zxsb_rail_popular_flag_icon zxsb_rail_popular_esports_icon" size={20} />
                      ) : (
                        <img
                          src={SPORT_ICON_BY_SLUG[league.sportSlug] || gameIconSoccer}
                          alt=""
                          className="zxsb_rail_popular_sport_icon"
                        />
                      )}
                      {!isEsportsPopular && flagSrc ? (
                        <img
                          src={flagSrc}
                          alt=""
                          className="zxsb_rail_popular_flag_icon"
                          onError={(event) => { event.currentTarget.style.display = 'none'; }}
                        />
                      ) : !isEsportsPopular ? (
                        <span className="zxsb_nav_flag_placeholder" />
                      ) : null}
                    </div>
                    <span className="zxsb_rail_popular_name">{league.nameKo || league.name}</span>
                    {league.fixtureCount > 0 && (
                      <span className="zxsb_rail_popular_count">{league.fixtureCount}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Mode Tabs */}
      <div className="zxsb_rail_tabs">
        {[
          { key: 'INTERNATIONAL', label: '해외형' },
          { key: 'LIVE',          label: '라이브' },
          { key: 'DOMESTIC',      label: '국내형' },
          { key: 'SPECIAL',       label: 'E-스포츠' },
        ].map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`zxsb_rail_tab ${activeTab === key ? 'zxsb_rail_tab_active' : ''}`}
            onClick={() => handleTabClick(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Content panels (slider) */}
      <div className="zxsb_rail_dropdown_mocks">
        <div className="zxsb_rail_dropdown_viewport">
          <div
            className="zxsb_rail_dropdown_slider"
            style={{ transform: `translateX(-${(TAB_INDEX_MAP[activeTab] || 0) * 25}%)` }}
          >
            {/* INTERNATIONAL panel */}
            <div className="zxsb_rail_dropdown_panel">
              {renderDynamicPanel()}
            </div>

            {/* LIVE panel */}
            <div className="zxsb_rail_dropdown_panel">
              {renderDynamicPanel()}
            </div>

            {/* DOMESTIC panel */}
            <div className="zxsb_rail_dropdown_panel">
              {renderDynamicPanel()}
            </div>

            {/* SPECIAL (E-스포츠) panel */}
            <div className="zxsb_rail_dropdown_panel">
              {renderEsportsPanel()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FigmaLeftRail;
